<script setup lang="ts">
import { computed } from 'vue';

export type IconName =
  'video' |
  'end-video' |
  'start-video' |
  'camera-switch' |
  'switch' |
  'attachment' |
  'send' |
  'chat' |
  'up' |
  'translate' |
  'close' |
  'rotate-phone'
;

const props = withDefaults(defineProps<{
  name: IconName;
  size?: number | string;
  color?: string;
}>(), {
  size: 20,
  color: 'currentColor'
});

// Icon viewBox and paths mapping
const icons: Record<IconName, { viewBox: string; paths: string[] }> = {
  'video': {
    viewBox: '0 0 20 20',
    paths: [
      'M19.4028 4.45973C19.0351 4.26931 18.5913 4.29445 18.2517 4.53261L14.6823 7.034C14.4306 7.20976 14.3698 7.55657 14.5456 7.80761C14.691 8.05903 15.066 8.11806 15.3194 7.94445L18.8889 5.44445V14.3299L15.3194 11.8333C15.0672 11.6581 14.7215 11.7183 14.5458 11.9701C14.3701 12.2213 14.4308 12.568 14.6826 12.7437L18.252 15.243C18.443 15.3765 18.6666 15.4439 18.8902 15.4439C19.0655 15.4439 19.2416 15.4021 19.4034 15.318C19.7708 15.125 20 14.7188 20 14.3021V5.44445C20 5.02778 19.7708 4.65278 19.4028 4.45973ZM11.1111 3.22223H2.22222C0.994792 3.22223 0 4.21702 0 5.44445V14.3333C0 15.5608 0.994792 16.5556 2.22222 16.5556H11.1111C12.3385 16.5556 13.3333 15.5608 13.3333 14.3333V5.44445C13.3333 4.21702 12.3368 3.22223 11.1111 3.22223ZM12.2222 14.3333C12.2222 14.9458 11.7236 15.4445 11.1111 15.4445H2.22222C1.60972 15.4445 1.11111 14.9458 1.11111 14.3333V5.44445C1.11111 4.83195 1.60972 4.33334 2.22222 4.33334H11.1111C11.7236 4.33334 12.2222 4.83195 12.2222 5.44445V14.3333Z'
    ]
  },
  'end-video': {
    viewBox: '0 0 20 20',
    paths: [
      'M19.9062 17.1625L4.45929 5.00003L3.95304 4.60316L0.718663 2.05628C0.612413 1.96878 0.453038 1.98753 0.368663 2.09378L0.0561626 2.48441C-0.0313374 2.59378 -0.0125874 2.75003 0.0936626 2.83753L13.7155 13.5625L13.9999 13.7875L19.2812 17.9469C19.3874 18.0344 19.5468 18.0157 19.6312 17.9094L19.9437 17.5188C20.0312 17.4063 20.0124 17.25 19.9062 17.1625ZM12.3812 15H2.61866C2.28429 15 1.99991 14.775 1.99991 14.5063V6.24691L1.00304 5.46253C1.00304 5.47503 0.999913 5.48441 0.999913 5.49691V14.5094C0.999913 15.3344 1.72491 16.0032 2.61866 16.0032H12.3812C12.8999 16.0032 13.3562 15.775 13.653 15.425L12.8624 14.8032C12.7468 14.9188 12.578 15 12.3812 15ZM12.3812 5.00003C12.7155 5.00003 12.9999 5.22503 12.9999 5.49378V9.81566L13.9999 10.6032V8.54691L17.9999 6.00003V13.7532L18.8874 14.4532C18.953 14.3219 18.9999 14.1782 18.9999 14.0157V5.98441C18.9999 5.40628 18.5155 5.00003 17.9968 5.00003C17.803 5.00003 17.6062 5.05628 17.428 5.17816L13.9999 7.36253V5.49378C13.9999 4.66878 13.2749 4.00003 12.3812 4.00003H5.61241L6.88116 5.00003H12.3812Z'
    ]
  },
  'start-video': {
    viewBox: '0 0 20 20',
    paths: [
      'M12.3812 15H2.61866C2.28429 15 1.99991 14.775 1.99991 14.5063V6.24691V5.49691C1.99991 5.22503 2.28429 5.00003 2.61866 5.00003H12.3812C12.7155 5.00003 12.9999 5.22503 12.9999 5.49378V7.36253L17.428 5.17816C17.6062 5.05628 17.803 5.00003 17.9968 5.00003C18.5155 5.00003 18.9999 5.40628 18.9999 5.98441V14.0157C18.9999 14.5938 18.5155 15 17.9968 15C17.803 15 17.6062 14.9438 17.428 14.8219L12.9999 12.6375V14.5063C12.9999 14.775 12.7155 15 12.3812 15Z'
    ]
  },
  'camera-switch': {
    viewBox: '0 -960 960 960',
    paths: [
      'M480-240q79 0 136-53.5T678-426l30 28 42-42-100-100-100 100 42 42 26-26q-6 53-45 88.5T480-300q-13 0-25.5-2.5T430-310l-44 44q22 12 45.5 19t48.5 7ZM310-340l100-100-42-42-26 26q6-53 45-88.5t93-35.5q13 0 25.5 2.5T530-570l44-44q-22-12-45.5-19t-48.5-7q-79 0-136 53.5T282-454l-30-28-42 42 100 100ZM160-120q-33 0-56.5-23.5T80-200v-480q0-33 23.5-56.5T160-760h126l74-80h240l74 80h126q33 0 56.5 23.5T880-680v480q0 33-23.5 56.5T800-120H160Zm0-80h640v-480H638l-73-80H395l-73 80H160v480Zm320-240Z'
    ]
  },
  'switch': {
    viewBox: '0 0 20 20',
    paths: [
      'M10.4999 13.5H4.49992V5.70623L7.14648 8.35279C7.24367 8.44998 7.3718 8.49998 7.49992 8.49998C7.62805 8.49998 7.7558 8.45117 7.85336 8.35348C8.04867 8.15817 8.04867 7.84161 7.85336 7.64661L4.35336 4.14661C4.15805 3.95129 3.84148 3.95129 3.64648 4.14661L0.146484 7.64661C-0.0488281 7.84192 -0.0488281 8.15848 0.146484 8.35348C0.341797 8.54848 0.658359 8.54879 0.853359 8.35348L3.49992 5.70623V14C3.49992 14.2764 3.72355 14.5 3.99992 14.5H10.4999C10.7763 14.5 10.9999 14.2764 10.9999 14C10.9999 13.7236 10.7749 13.5 10.4999 13.5ZM19.853 11.6469C19.6577 11.4515 19.3412 11.4515 19.1462 11.6469L16.4999 14.2937V5.99998C16.4999 5.72361 16.2763 5.49998 15.9999 5.49998H9.49992C9.22492 5.49998 8.99992 5.72498 8.99992 5.99998C8.99992 6.27498 9.22355 6.49998 9.49992 6.49998H15.4999V14.2937L12.8534 11.6472C12.658 11.4519 12.3415 11.4519 12.1465 11.6472C11.9515 11.8425 11.9512 12.159 12.1465 12.354L15.6465 15.854C15.7437 15.95 15.8718 16 15.9999 16C16.128 16 16.2558 15.9512 16.3534 15.8535L19.8534 12.3535C20.0499 12.1594 20.0499 11.8406 19.853 11.6469Z'
    ]
  },
  'attachment': {
    viewBox: '0 0 20 20',
    paths: [
      'M15.8712 2.62926C14.7344 1.49293 12.8907 1.49293 11.754 2.62926L4.25437 10.1289C2.42742 11.957 2.42742 14.9179 4.25437 16.7461C6.0821 18.5742 9.04304 18.5742 10.8712 16.7461L16.8087 10.8086C17.0509 10.5625 17.4493 10.5625 17.6915 10.8086C17.9376 11.0507 17.9376 11.4492 17.6915 11.6914L11.754 17.6289C9.43757 19.9453 5.65242 19.9453 3.37078 17.6289C1.05542 15.3125 1.05546 11.5273 3.37078 9.24606L10.8712 1.74567C12.4962 0.120747 15.129 0.120747 16.754 1.74567C18.379 3.37028 18.379 6.00387 16.754 7.62887L9.57429 14.8086C8.47273 15.9101 6.66023 15.8086 5.65242 14.5937C4.85437 13.5195 4.93757 12.0547 5.87898 11.1132L11.8087 5.18356C12.0509 4.93747 12.4493 4.93747 12.6915 5.18356C12.9376 5.42575 12.9376 5.82418 12.6915 6.06637L6.76179 11.9961C6.23835 12.4882 6.22664 13.2695 6.62898 13.8125C7.13679 14.4453 8.11726 14.5 8.69148 13.9257L15.8712 6.74606C17.0079 5.60934 17.0079 3.76598 15.8712 2.62926Z'
    ]
  },
  'send': {
    viewBox: '0 0 20 20',
    paths: [
      'M19.2418 8.85156L1.74215 1.35117C1.58356 1.2832 1.41638 1.21484 1.25076 1.21484C0.913928 1.21484 0.582802 1.35152 0.340619 1.60742C-0.0194636 2.02383 -0.102458 2.58984 0.131912 3.05898L3.74993 10L0.13203 16.9414C-0.102341 17.4109 -0.0193347 17.9766 0.340775 18.3582C0.58249 18.6139 0.913421 18.7508 1.25013 18.7508C1.41615 18.7508 1.58337 18.7178 1.74192 18.6495L19.2418 11.1484C19.702 10.9519 20 10.4996 20 9.99961C19.9645 9.5 19.7027 9.04688 19.2418 8.85156ZM1.2492 2.50117C1.24841 2.50156 1.24959 2.50039 1.2492 2.50117L17.2926 9.375H4.83194L1.2492 2.50117ZM1.24021 17.5195L4.83194 10.625H17.2575L1.24021 17.5195Z'
    ]
  },
  'chat': {
    viewBox: '0 0 36 36',
    paths: [
      'M32.4 10.8H25.2V12.6H32.4C33.3922 12.6 34.2 13.4077 34.2 14.4V25.2C34.2 26.1922 33.3922 27 32.4 27H28.8V29.7883L23.8725 27H18C17.0077 27 16.2 26.1922 16.2 25.2V23.4L14.4 23.3998V25.1986C14.4 27.1814 16.0172 28.748 17.9494 28.748L23.4 28.8L29.5256 32.2667C29.6494 32.3606 29.79 32.4 29.925 32.4C30.2794 32.4 30.6 32.1243 30.6 31.725V28.7983H32.4C34.3828 28.7983 36 27.1811 36 25.2489V14.3494C36 12.4144 34.3856 10.8 32.4 10.8ZM23.4 18V7.14935C23.4 5.21716 21.7856 3.59998 19.8 3.59998H3.6C1.61719 3.59998 0 5.21716 0 7.14935V17.9494C0 19.9856 1.61719 21.6 3.6 21.6L5.4 21.6006V24.525C5.4 24.9244 5.72625 25.2 6.075 25.2C6.20859 25.2 6.34922 25.1578 6.47578 25.0664L12.6 21.6L19.8 21.5958C21.7856 21.5943 23.4 19.9856 23.4 18ZM12.1275 19.8L7.2 22.59V19.8H3.6C2.60775 19.8 1.8 18.99 1.8 18V7.19998C1.8 6.20773 2.60775 5.39998 3.6 5.39998H19.8C20.7922 5.39998 21.6 6.20773 21.6 7.19998V18C21.6 18.9922 20.7922 19.8 19.8 19.8H12.1275Z'
    ]
  },
  'up': {
    viewBox: '0 -960 960 960',
    paths: [
      'M440-160v-487L216-423l-56-57 320-320 320 320-56 57-224-224v487h-80Z'
    ]
  },
  'translate': {
    viewBox: '0 -960 960 960',
    paths: [
      'M180-398h27l22-60h118l22 60h28l-91-242h-36l-90 242Zm60-87 47-127h2l47 127h-96Zm314-95v-28q32-17 69.5-25.5T700-642q21 0 40 2.5t40 7.5v28q-20-6-38.5-8t-41.5-2q-39 0-76.5 8.5T554-580Zm0 218v-30q30-17 69-25.5t77-8.5q21 0 40 2.5t40 7.5v28q-20-6-38.5-8t-41.5-2q-39 0-76.5 9.5T554-362Zm0-108v-30q32-17 69.5-25.5T700-534q21 0 40 2.5t40 7.5v28q-20-6-38.5-8t-41.5-2q-39 0-76.5 9.5T554-470ZM260-318q54 0 105 12.5T466-264v-392q-44-33-98-49.5T260-722q-36 0-60.5 3.5T142-704q-12 4-17 11.5t-5 16.5v350q0 14 10 20.5t22 1.5q18-6 45-10t63-4Zm234 54q50-29 101-41.5T700-318q36 0 63 4t45 10q12 5 22-1.5t10-20.5v-350q0-9-5-16t-17-12q-33-11-57.5-14.5T700-722q-54 0-108 16.5T494-656v392Zm-14 40q-49-32-105-49t-115-17q-28 0-55 3.5T152-274q-22 8-41-6.5T92-320v-358q0-16 8.5-29.5T124-726q32-13 66.5-18.5T260-750q59 0 115 17.5T480-682q49-33 105-50.5T700-750q35 0 69.5 5.5T836-726q15 5 23.5 18.5T868-678v358q0 25-21 38.5t-45 5.5q-25-8-50.5-11t-51.5-3q-59 0-115 17t-105 49ZM293-493Z'
    ]
  },
  'close': {
    viewBox: '0 -960 960 960',
    paths: [
      'm256-200-56-56 224-224-224-224 56-56 224 224 224-224 56 56-224 224 224 224-56 56-224-224-224 224Z'
    ]
  },
  'rotate-phone': {
    viewBox: '0 0 24 24',
    paths: [
      'M16.48 2.52c3.27 1.55 5.61 4.72 5.97 8.48h1.5C23.44 4.84 18.29 0 12 0l-.66.03 3.81 3.81zm-6.25-.77c-.59-.59-1.54-.59-2.12 0L1.75 8.11c-.59.59-.59 1.54 0 2.12l12.02 12.02c.59.59 1.54.59 2.12 0l6.36-6.36c.59-.59.59-1.54 0-2.12zm4.6 19.44L2.81 9.17l6.36-6.36 12.02 12.02zm-7.31.29C4.25 19.94 1.91 16.76 1.55 13H.05C.56 19.16 5.71 24 12 24l.66-.03-3.81-3.81z'
    ]
  }
};

const iconData = computed(() => icons[props.name]);

const sizeValue = computed(() => {
  return typeof props.size === 'number' ? `${props.size}px` : props.size;
});
</script>

<template>
  <svg
    :width="sizeValue"
    :height="sizeValue"
    :viewBox="iconData.viewBox"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    class="base-icon"
  >
    <path
      v-for="(path, index) in iconData.paths"
      :key="index"
      :d="path"
      :fill="color"
    />
  </svg>
</template>

<style scoped>
.base-icon {
  display: inline-block;
  vertical-align: middle;
  flex-shrink: 0;
}
</style>